import React, { useEffect, useState, useRef } from "react";
import { navigate } from "raviger";
import { toast } from 'react-toastify';
import "./Authenticate.css";
import AuthBrandPanel from "./AuthBrandPanel";
import backArrow from "./../../../assets/img/auth-back-arrow.svg";
import FormRenderer, { ComponentMapper, FormTemplate } from "../../common/FormRenderer";
import PageLoader from "../../shared/PageLoader";
import { LOGIN_FIELDS } from "./Authenticate.constants";
import { useSSOCheck, useSSOInit } from "../../../backend/auth";
import { getBasePath } from "../../../App";
import { AxiosError } from "axios";
import { setUserSession } from "../../../backend/auth";

function SSOLogin() {
    const [form, setForm] = useState<any>({});
    const [code, setCode] = useState('');
    const ssoPopupRef = useRef<Window | null>(null);
    const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;

            if (event.data && typeof event.data === 'object') {
                if (event.data.type === 'SSO_SUCCESS') {
                    if (ssoPopupRef.current && !ssoPopupRef.current.closed) {
                        ssoPopupRef.current.close();
                    }

                    if (event.data.token) {
                        try {
                            setUserSession(event.data.token);
                            navigate('/');
                            window.location.reload();
                        } catch (error) {
                            toast.error('Failed to process SSO token');
                        }
                    }
                } else if (event.data.type === 'SSO_ERROR') {
                    if (ssoPopupRef.current && !ssoPopupRef.current.closed) {
                        ssoPopupRef.current.close();
                    }

                    toast.error(event.data.error || 'SSO login failed. Please try again.');
                }
            }
        };

        messageHandlerRef.current = handleMessage;

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
            if (ssoPopupRef.current && !ssoPopupRef.current.closed) {
                ssoPopupRef.current.close();
            }
        };
    }, []);
    
    const schema = {
        fields: [{ ...LOGIN_FIELDS.COMPANY_CODE, className: 'ciel-auth-field' }]
    };

    const { checkSSO, isLoading: checkingSSO } = useSSOCheck(({ sso_available }: any) => {
        if (sso_available) {
            // Initiate SSO if available
            const redirectUrl = `${getBasePath()}/auth/callback`;
            initSSO({ code, redirectUrl });
        } else {
            toast.error('SSO is not enabled for this company');
        }
    }, errorCallback);

    const { initSSO, isLoading: initiatingSSO } = useSSOInit(({ redirect_url }: any) => {
        if (redirect_url) {
            if (ssoPopupRef.current && !ssoPopupRef.current.closed) {
                ssoPopupRef.current.close();
            }

            const windowWidth = 600;
            const windowHeight = 700;
            const left = (window.screen.width - windowWidth) / 2;
            const top = (window.screen.height - windowHeight) / 2;

            const popup = window.open(
                redirect_url,
                'EzycompSSOLogin',
                `width=${windowWidth},height=${windowHeight},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
            );

            if (popup) {
                ssoPopupRef.current = popup;

                setTimeout(() => {
                    if (!popup || popup.closed) {
                        toast.error('Popup was blocked. Please allow popups for this site.');
                    }
                }, 1000);

                const checkPopupInterval = setInterval(() => {
                    if (popup.closed) {
                        clearInterval(checkPopupInterval);
                        ssoPopupRef.current = null;
                    }
                }, 1000);
            } else {
                toast.error('Failed to open SSO login window. Please check if popups are blocked.');
            }
        } else {
            toast.error('Failed to initiate SSO login');
        }
    }, errorCallback);

    function errorCallback() {
        toast.error('An error occurred. Please try again.');
    }

    function handleSubmit({ code }: any) {
        setCode(code);
        checkSSO({ code });
    }

    function goBackToLogin() {
        navigate('/login');
    }

    return (
        <>
            <div className="row m-0 ciel-auth">
                <div className="col-md-8 p-0 ciel-auth-brand-col">
                    <AuthBrandPanel />
                </div>

                <div className="col-md-4 p-0 ciel-auth-form-col">
                    <div className="ciel-auth-card">
                        <button type="button" className="ciel-auth-back-link" onClick={goBackToLogin}>
                            <img src={backArrow} alt="" />
                            Back to Login page
                        </button>
                        <div className="ciel-auth-heading">
                            <h2>Login with SSO</h2>
                        </div>
                        <FormRenderer
                            FormTemplate={FormTemplate}
                            initialValues={{ submitBtnText: 'Continue with SSO', fullWidth: true }}
                            componentMapper={ComponentMapper}
                            schema={schema}
                            debug={setForm}
                            onSubmit={handleSubmit}
                            clearOnUnmount={true}
                        />
                    </div>
                </div>
            </div>
            {
                (checkingSSO || initiatingSSO) && <PageLoader>
                    {
                        checkingSSO && <p>Checking SSO availability...</p>
                    }
                    {
                        initiatingSSO && <p>Initiating SSO login...</p>
                    }
                </PageLoader>
            }
        </>
    );
}

export default SSOLogin;
